<script setup lang="ts">
import AppContextRail from '@/components/app-shell/AppContextRail.vue'
import FinestGauge from '@/components/FinestGauge.vue'
import FinestGaugeCompact from '@/components/FinestGaugeCompact.vue'
import { useI18n } from 'vue-i18n'

withDefaults(
  defineProps<{
    completion?: number
    context?: 'profile' | 'credit' | 'company' | 'companies' | 'property'
    showGauge?: boolean
    showDocuments?: boolean
  }>(),
  {
    completion: 0,
    context: 'profile',
    showGauge: true,
    showDocuments: true,
  },
)

const { t } = useI18n()
</script>

<template>
  <AppContextRail v-if="showGauge">
    <FinestGaugeCompact :value="completion" :label="t('gaugeLabel', { percent: completion })" />
  </AppContextRail>

  <div class="flex flex-col gap-6">
    <div v-if="showGauge" class="text-center">
      <FinestGauge :value="completion" :caption="t('gaugeCaption')" :label="t('gaugeLabel', { percent: completion })" />
      <p class="text-ink-2 mt-1.5 text-sm leading-relaxed">{{ t(`intro.${context}`) }}</p>
    </div>

    <div v-if="showDocuments" class="border-line border-t pt-4">
      <div class="mb-3 flex items-center justify-between">
        <p class="text-ink font-medium">{{ t('documents.title') }}</p>
      </div>
      <Message severity="info" icon="pi pi-info-circle" size="small">
        <span class="text-sm">{{ t('documents.inDevelopment') }}</span>
      </Message>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "gaugeCaption": "bankreif",
    "gaugeLabel": "{percent} % bankreif",
    "intro": {
      "profile": "So bankreif ist dein Profil. Vollständige Angaben und Dokumente beschleunigen die Prüfung deiner Finanzierung.",
      "credit": "So bankreif ist deine Bonität. Einkommen, Ausgaben und Vermögen beschleunigen die Prüfung deiner Finanzierung.",
      "company": "So vollständig sind die Angaben dieses Unternehmens. Vollständige Stammdaten beschleunigen die Prüfung deiner Finanzierung.",
      "companies": "So bankreif sind deine Unternehmen. Vollständige Stammdaten beschleunigen die Prüfung deiner Finanzierung.",
      "property": "So bankreif ist diese Immobilie. Vollständige Objektdaten beschleunigen die Prüfung deiner Finanzierung."
    },
    "documents": {
      "title": "Dokument-Assistent",
      "inDevelopment": "Der Dokument-Assistent ist aktuell in Entwicklung und bald verfügbar.",
      "open": "{count} offen",
      "missing": "Fehlt noch",
      "upload": "Upload",
      "dropzone": "Datei ablegen oder",
      "dropzoneAction": "hochladen",
      "fileCount": "{count} Dateien",
      "items": {
        "id_card": "Personalausweis",
        "salary_statements": "Gehaltsnachweise (3 Monate)",
        "tax_assessment": "Einkommensteuerbescheid",
        "schufa": "SCHUFA-Selbstauskunft",
        "bank_statements": "Kontoauszüge (3 Monate)"
      }
    }
  },
  "en": {
    "gaugeCaption": "bank-ready",
    "gaugeLabel": "{percent} % bank-ready",
    "intro": {
      "profile": "This is how bank-ready your profile is. Complete details and documents speed up the review of your financing.",
      "credit": "This is how bank-ready your credit rating is. Income, expenses and assets speed up the review of your financing.",
      "company": "This is how complete this company's data is. Complete master data speeds up the review of your financing.",
      "companies": "This is how bank-ready your companies are. Complete master data speeds up the review of your financing.",
      "property": "This is how bank-ready this property is. Complete property data speeds up the review of your financing."
    },
    "documents": {
      "title": "Document assistant",
      "inDevelopment": "The document assistant is currently in development and will be available soon.",
      "open": "{count} open",
      "missing": "Still missing",
      "upload": "Upload",
      "dropzone": "Drop a file or",
      "dropzoneAction": "upload",
      "fileCount": "{count} files",
      "items": {
        "id_card": "ID card",
        "salary_statements": "Salary statements (3 months)",
        "tax_assessment": "Income tax assessment",
        "schufa": "SCHUFA credit report",
        "bank_statements": "Bank statements (3 months)"
      }
    }
  }
}
</i18n>
